import { applicationBadgeState } from 'tio-employee/utils/tas/applicationStateMap';
import { array, hash } from '@ember/helper';
import { Button } from 'tio-ui/components/buttons';
import Chip from 'tio-reimagined/components/chip';
import type { ChipSignature } from 'tio-reimagined/components/chip';
import { dropTask } from 'ember-concurrency';
import { getSingleValueForTasField } from 'tio-common/utils/tuition-assistance/fields';
import { htmlSafe } from '@ember/template';
import { instanceBadgeState } from 'tio-employee/utils/tas/instanceStateMap';
import { LinkTo } from '@ember/routing';
import { on } from '@ember/modifier';
import { and, not, or } from 'tio-ui/utilities';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import { type TBodyOptions } from 'tio-ui/components/table';
import { VStack, HStack, Section } from 'tio-ui/components/layout';
import Component from '@glimmer/component';
import CourseCashTag from 'tio-common/components/tas/course-cash-tag';
import formatCentsToDollars from 'tio-common/helpers/format-cents-to-dollars';
import lastFourDigits from 'tio-common/helpers/last-four-digits';
import NavTabs from 'tio-ui/components/nav-tabs';
import safeFormatDate from 'tio-common/helpers/safe-format-date';
import StartDate from 'tio-employee/components/tas/start-date-eligibility';
import Table from 'tio-ui/components/table';
import TASProgramDetailsWithdrawComplete from 'tio-employee/components/tuition-assistance/program-details/withdraw-complete';
import TioAlert from 'tio-common/components/tio/alert';
import type { Step } from 'tio-common/components/tio/progress-tracker';
import type { TasProgramInstanceState } from 'tio-common/types/tuition-assistance';
import type { TOC } from '@ember/component/template-only';
import type IntlService from 'ember-intl/services/intl';
import type ProgressBuilder from 'tio-common/services/progress-builder';
import type RouterService from '@ember/routing/router';
import type SessionContextService from 'tio-employee/services/session-context';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';
import type TasApplicationModel from 'tio-common/models/tas-application';
import type TasParticipantModel from 'tio-common/models/tas-participant';
import type TASProgramInstanceModel from 'tio-common/models/tas-program-instance';
import type TuitionAssistanceService from 'tio-common/services/tuition-assistance';
import VerticalProgressTracker from 'tio-common/components/tio/vertical-progress-tracker';

interface RouteSignature {
  Args: {
    model: {
      activeInstances: TASProgramInstanceModel[];
      programInstance: TASProgramInstanceModel;
      tasParticipant: TasParticipantModel;
    };
  };
}

interface ApplicationRowSignature {
  Args: {
    options: typeof TBodyOptions;
    appName: string;
    programInstanceBadgeState: Record<string, ChipSignature['Args']['intent']>;
    application: TasApplicationModel;
    appId: string;
  };
}

const ApplicationRow: TOC<ApplicationRowSignature> = <template>
  <@options.tr>
    <@options.td>
      <LinkTo
        @route="authenticated.tas.applications.show"
        @model={{@application.id}}
        class="flex items-end gap-x-4"
      >
        <span class="text-xl font-medium underline text-ocean-600">{{or
            @appName
            "Untitled Application"
          }}</span>
        <Chip @intent={{@programInstanceBadgeState.intent}}>
          {{@programInstanceBadgeState.label}}
        </Chip>
      </LinkTo>
      <p class="inline-block text-sm text-tio-gray-400 self-end">{{t
          "tas.application.id_no"
        }}{{@appId}}</p>
    </@options.td>
    <@options.td>{{@application.tasCourses.length}} {{t "tas.program.courses"}}</@options.td>
    <@options.td>
      {{#if @application.isCreditBased}}
        {{@application.totalCredits}}
        {{t "common.credits"}}
      {{else}}
        {{formatCentsToDollars @application.requestedTotal}}
      {{/if}}
    </@options.td>
  </@options.tr>
</template>;

export default class AuthenticatedTasProgramsShow extends Component<RouteSignature> {
  @service declare intl: IntlService;
  @service declare progressBuilder: ProgressBuilder;
  @service declare router: RouterService;
  @service declare sessionContext: SessionContextService;
  @service declare store: StoreService;
  @service declare tuitionAssistance: TuitionAssistanceService;

  @tracked isOpen = true;

  appBadgeState(application: TasApplicationModel) {
    return applicationBadgeState(
      application.state,
      // @ts-expect-error: seems like a bug
      application.programInstance?.approvalRequired
    ) as Record<string, ChipSignature['Args']['intent']>;
  }

  get programTemplate() {
    return this.args.model.programInstance.tasProgramTemplate;
  }

  get programInstanceBadgeState() {
    const approvalRequired = this.programTemplate.programApprovalRequired;
    return instanceBadgeState(this.args.model.programInstance.state, approvalRequired) as Record<
      string,
      ChipSignature['Args']['intent']
    >;
  }

  get institution() {
    return getSingleValueForTasField(
      'SCHOOL_INSTITUTION_NAME',
      this.args.model.programInstance.fields
    ) as string;
  }

  get isInProgress() {
    return this.args.model.programInstance.programInstanceInProgress;
  }

  get steps() {
    return this.progressBuilder.applicationProcess() as Step[];
  }

  get programProgress() {
    return this.progressBuilder.programProgress(this.args.model.programInstance) as Step[];
  }

  get hasActiveApplications() {
    return !!this.args.model.programInstance.tasApplications.length;
  }

  get isHeldParticipant() {
    // @ts-expect-error: seems like a bug
    return !!this.args.model.tasParticipant.isHeldParticipant;
  }

  get isActiveEligibility() {
    return this.tuitionAssistance.hasActiveEligibilityForProgramTemplate(this.programTemplate);
  }

  get ineligibleBasedOnWaitingPeriod() {
    return this.tuitionAssistance.ineligibleBasedOnWaitingPeriod(
      this.programTemplate.eligibilityWaitingPeriod,
      this.args.model.tasParticipant
    );
  }

  get isSingleApplicationProgram() {
    return !!getSingleValueForTasField('SINGLE_APPLICATION_PROGRAM', this.programTemplate.fields);
  }

  applicationName(application: TasApplicationModel) {
    return application.displayNameOrSemesterCode as string;
  }

  get canCreateApplications() {
    const { programApprovalRequired } = this.programTemplate;

    const validStates: TasProgramInstanceState[] = [
      'TAS.ProgramInstanceState.PRIMED',
      'TAS.ProgramInstanceState.PENDING_COMPLETION_APPROVAL',
    ];

    if (!programApprovalRequired) {
      validStates.push(
        'TAS.ProgramInstanceState.PROGRAM_APPROVED',
        'TAS.ProgramInstanceState.PENDING_PROGRAM_APPROVAL'
      );
    }
    return validStates.includes(this.args.model.programInstance.state);
  }

  get programHasExistingApplications() {
    return !!this.args.model.programInstance?.tasApplications?.length;
  }

  get typeClassification() {
    return this.programTemplate.typeClassification;
  }

  get canApplyForApplication() {
    if (
      this.programTemplate.state === 'TAS.ProgramTemplateState.SUSPENDED' ||
      this.typeClassification === 'TAS.ProgramType.1' ||
      this.isHeldParticipant ||
      !this.isActiveEligibility ||
      !this.sessionContext.currentEmployee.id
    ) {
      return false;
    } else if (this.isSingleApplicationProgram) {
      return this.canCreateApplications && !this.programHasExistingApplications;
    }
    return this.canCreateApplications;
  }

  get isPendingApproval() {
    if (!this.programTemplate.programApprovalRequired) {
      return false;
    }
    return (
      this.args.model.programInstance.state === 'TAS.ProgramInstanceState.PENDING_PROGRAM_APPROVAL'
    );
  }

  get showEligibility() {
    let lock = getSingleValueForTasField(
      'LOCK_EMPLOYEE_STATUS_BASED_ON',
      this.programTemplate.fields
    ) as string;
    const hasLock = () => ['COURSE_START_DATE', 'COURSE_END_DATE'].includes(lock);
    return hasLock();
  }

  get programTemplateName() {
    return getSingleValueForTasField('PROGRAM_NAME', this.programTemplate.fields) as string;
  }

  get isProgramLaunched() {
    return this.programTemplate.state === 'TAS.ProgramTemplateState.LAUNCHED';
  }

  get tasApplicationsSorted() {
    const apps = this.args.model.programInstance.tasApplications ?? [];

    const nonAbandoned = apps.filter((a) => a.state !== 'TAS.ApplicationState.ABANDONED');
    const abandoned = apps.filter((a) => a.state === 'TAS.ApplicationState.ABANDONED');

    return [...nonAbandoned, ...abandoned];
  }

  newApplicationAction = () => {
    const date = new Date().toLocaleDateString('en-CA');
    this.createApplicationAndApply.perform(date);
  };

  createApplicationAndApply = dropTask(async (date?: string) => {
    const route = 'authenticated.tas.applications.type-four.new';
    try {
      const application = this.store.createRecord<TasApplicationModel>('tas-application', {
        tasProgramInstance: this.args.model.programInstance,
      });
      await application.save();
      const queryParams: Record<string, unknown> = {};
      if (date) {
        queryParams.date = date;
      }
      this.router.transitionTo(route, application.id, { queryParams });
    } catch (error) {
      console.error('Failed to save application:', error);
    }
  });

  <template>
    <VStack class="w-full">
      <NavTabs
        @tabs={{array
          (hash label=(t "tas.program.program") route="authenticated.tas.programs.show")
          (hash label=(t "tas.program.details") route="authenticated.tas.programs.details")
          (hash label=(t "tas.program.history") route="authenticated.tas.programs.history")
        }}
      />
      {{#if
        (and
          this.programTemplate.isDirectPayProgram
          this.programTemplate.isSuspended
          this.programTemplate.suspendedProgramDisclosure
        )
      }}
        <TioAlert @type="warning" @allowDismiss={{false}}>
          <:header>
            <p class="font-bold">
              {{htmlSafe (t "tuition_assistance.alerts.coursecash_program_update")}}
            </p>
          </:header>
          <:body>
            <p class="text-sm">
              {{this.programTemplate.suspendedProgramDisclosure}}
            </p>
          </:body>
        </TioAlert>
      {{/if}}
      <HStack>
        <Section class="w-full lg:basis-2/3">
          <:header>
            <div class="flex flex-row justify-between">
              <div class="flex items-center">
                <p class="mr-4">{{this.programTemplateName}}</p>
                <Chip @intent={{this.programInstanceBadgeState.intent}}>
                  {{this.programInstanceBadgeState.label}}
                </Chip>
              </div>
            </div>
          </:header>
          <:actions>
            <TASProgramDetailsWithdrawComplete
              @programInstance={{@model.programInstance}}
              @isHeldParticipant={{@model.tasParticipant.isHeld}}
            />
          </:actions>
          <:body>
            <VStack>
              <p>{{@model.programInstance.institutionName}}</p>
              {{#if @model.programInstance.estimatedProgramBegin}}
                <p>
                  {{safeFormatDate @model.programInstance.estimatedProgramBegin}}
                  -
                  {{safeFormatDate @model.programInstance.estimatedProgramCompletion}}
                </p>
              {{else}}
                <p class="italic">{{t "tas.program.start_date_not_set"}}</p>
              {{/if}}
              <CourseCashTag
                @isDirectPayProgram={{this.programTemplate.isDirectPayProgram}}
                @size="sm"
              >
                <:beforeTag>
                  <span>
                    {{t "common.tas.course_cash.eligible_for"}}
                  </span>
                </:beforeTag>
              </CourseCashTag>
              {{#if this.hasActiveApplications}}
                <VStack @collapsed={{true}}>
                  <Section>
                    <:body>
                      <div class="w-full flex justify-between items-center">
                        <h2 class="text-lg font-medium">
                          {{t "tuition_assistance.applications.default"}}
                        </h2>
                        {{#if this.canApplyForApplication}}
                          <Button
                            @intent="primary"
                            {{on "click" this.newApplicationAction}}
                            disabled={{not this.isProgramLaunched}}
                          >
                            {{t "tas.program.new_application"}}
                          </Button>
                        {{/if}}
                      </div>
                      <Table>
                        <:thead as |options|>
                          <options.tr>
                            <options.th>{{t "tuition_assistance.application_name"}}</options.th>
                            <options.th>
                              {{t "tuition_assistance.program_details.courses.default"}}
                            </options.th>
                            <options.th>
                              {{t "tuition_assistance.program_details.courses.requested_total"}}
                            </options.th>
                          </options.tr>
                        </:thead>
                        <:tbody as |options|>
                          {{#each this.tasApplicationsSorted as |application|}}
                            <ApplicationRow
                              @options={{options}}
                              @appName={{this.applicationName application}}
                              @programInstanceBadgeState={{this.appBadgeState application}}
                              @application={{application}}
                              @appId={{lastFourDigits application.id}}
                            />
                          {{/each}}
                        </:tbody>
                      </Table>
                    </:body>
                  </Section>
                </VStack>
              {{else if this.isPendingApproval}}
                <VStack @collapsed={{true}}>
                  <Section>
                    <TioAlert @type="primary" @allowDismiss={{false}} class="mb-4">
                      <:header>
                        <p class="font-bold">
                          {{t "tas.program.pending_program_approval"}}
                        </p>
                      </:header>
                      <:body>
                        <p class="text-sm text-violet-700">
                          {{t "tas.program.pending_program_approval_description"}}
                        </p>
                      </:body>
                    </TioAlert>
                  </Section>
                </VStack>
              {{else if this.canApplyForApplication}}
                <VStack @collapsed={{true}}>
                  <Section>
                    <h2 class="mb-4 text-lg font-medium">{{t "tas.program.apply_for_courses"}}</h2>
                    <p class="mb-4">{{t "tas.program.course_application_description"}}</p>
                    {{#if this.showEligibility}}
                      <StartDate
                        @canApplyToProgram={{and
                          this.canApplyForApplication
                          this.isProgramLaunched
                        }}
                        @buttonText="Get Started"
                        @isMultipleInstanceProgram={{true}}
                        @programTemplate={{this.programTemplate}}
                        @tasParticipant={{@model.tasParticipant}}
                        @applyToProgram={{this.createApplicationAndApply.perform}}
                      />
                    {{else}}
                      <Button
                        @intent="primary"
                        {{on "click" this.newApplicationAction}}
                        class="uppercase w-60"
                        disabled={{not this.isProgramLaunched}}
                        data-test-program-get-started-button
                      >
                        {{t "login.get_started"}}
                      </Button>
                    {{/if}}
                  </Section>
                  <Section>
                    <h2 class="mb-4 text-lg font-medium">
                      {{t "tas.program.course_application_process"}}
                    </h2>
                    <VerticalProgressTracker class="m-6" @steps={{this.steps}} />
                  </Section>
                </VStack>
              {{/if}}
            </VStack>
          </:body>
        </Section>
        <Section class="w-full lg:basis-1/3">
          <:header>{{t "common.progress_tracker.program_progress"}}</:header>
          <:body>
            {{! TODO: using lists to show numbers causes flow issues. The ml-4 class will force
                the numbers into the box, but this isn't ideal as mobile view will have issues.
                Looks like the component is just a stack anyway so might be target for update. }}
            <VerticalProgressTracker class="ml-4" @steps={{this.programProgress}} />
          </:body>
        </Section>
      </HStack>
    </VStack>
  </template>
}
